/**
 * Bulgarian is the primary interface language, English is secondary.
 *
 * CLAUDE.md requires `Intl` formatting pinned to `bg-BG` / `Europe/Sofia`.
 * Nothing in the product formats a date or a number yet, so there is nothing
 * here to pin: a `TIME_ZONE = 'Europe/Sofia'` constant lived at this spot with
 * no importer. Put it back — here, not at the call site — in the change that
 * renders the first date. `playwright.config.ts` sets the same zone for the
 * browser, and the two should be able to be compared.
 */

export const LOCALES = ['bg', 'en'] as const
export type Locale = (typeof LOCALES)[number]

export const DEFAULT_LOCALE: Locale = 'bg'

export function isLocale(value: string): value is Locale {
  return (LOCALES as readonly string[]).includes(value)
}

/**
 * The header the client puts the interface language in.
 *
 * Lower case because that is how a `Headers` lookup normalises it, and because
 * it is compared as a literal in both the Worker and the tests.
 */
export const LOCALE_HEADER = 'x-app-locale'

/**
 * Which language a transactional message is written in.
 *
 * ## Why this reads one header and nothing else
 *
 * The only thing that reliably says which language a reader wants is the
 * language the site was showing them when they pressed the button. That is
 * what the client sends in `X-App-Locale`, on every call to the auth API.
 *
 * `Accept-Language` is deliberately not consulted, not even as a second
 * choice. It describes the browser installation, not the reader: an English
 * Windows is entirely ordinary in Bulgaria, and negotiating on that header is
 * how a reader who had switched this site to Bulgarian was sent an English
 * verification email. It also cannot change when they use the language
 * switcher, so it would go on being wrong for as long as the account existed.
 *
 * `profiles.locale` is not consulted either. At registration there is no
 * profile row yet, and the verification message is the one that matters most;
 * a rule that covered two of the three templates and not the third is how the
 * three drift apart.
 *
 * ## When it is absent or unrecognised
 *
 * Bulgarian, on purpose and in one place. Missing header, empty header,
 * `de-DE`, `BG-bg`, a request made from somewhere that is not the browser —
 * all of them land on Bulgarian, because this is a Bulgarian site and the
 * wrong answer should be the Bulgarian one. Written as "is this exactly
 * English?" rather than "is this not Bulgarian?", so a garbled value cannot
 * fall through to the secondary language.
 */
export function mailLocale(header: string | null | undefined): Locale {
  const value = (header ?? '').trim().toLowerCase()
  // A single well-formed language tag and nothing else. A comma-separated
  // Accept-Language list fails here rather than having its first entry
  // picked out, so a client that sends the wrong header gets Bulgarian
  // instead of an accidental negotiation.
  if (!/^[a-z]{2,8}(?:-[a-z0-9]{1,8})*$/.test(value)) return DEFAULT_LOCALE
  return value.split('-')[0] === 'en' ? 'en' : DEFAULT_LOCALE
}

/**
 * The query parameter a link carries the interface language in.
 *
 * It selects between two translation tables and nothing else. It is not a
 * credential, it is not trusted for anything, and an unrecognised value is
 * ignored rather than rejected — see `currentLocale`.
 */
export const LOCALE_PARAM = 'lang'

/**
 * Carry a language forward on a link.
 *
 * ## Why a link needs this at all
 *
 * The chosen language lives in `localStorage`, which belongs to one origin in
 * one browser profile. A reader who follows a link out of their mail client
 * opens it somewhere that has none of it, so the site falls back to what the
 * browser asks for — and an English Windows is entirely ordinary in Bulgaria.
 * That is the same reader, told in Bulgarian to confirm an address, landing
 * on an English page.
 *
 * `mailLocale` already worked out which language the message is written in.
 * This puts that same value, unchanged, on the link inside it. Nothing
 * recomputes it and nothing consults `Accept-Language`.
 *
 * ## Which URL gets tagged
 *
 * The one the reader ends up looking at. Better Auth's links point at its own
 * endpoint, which verifies the token and then redirects to the `callbackURL`
 * it was given, so the parameter goes on that inner URL; anything left of it
 * is machinery the reader never sees. A link with no `callbackURL` — the
 * "your password was changed" notice, which points straight at a page — is
 * tagged directly.
 */
export function withLocale(url: string, locale: Locale): string {
  try {
    const parsed = new URL(url)
    const callback = parsed.searchParams.get('callbackURL')
    if (callback) {
      parsed.searchParams.set('callbackURL', tagged(callback, locale))
    } else {
      parsed.searchParams.set(LOCALE_PARAM, locale)
    }
    return parsed.href
  } catch {
    // Every URL this is given is built by Better Auth from `baseURL`, so an
    // unparseable one should not happen. If it ever does, the link goes out
    // as it was: a message that opens in the wrong language is a nuisance,
    // and a registration that fails because a URL could not be parsed is a
    // broken product.
    return url
  }
}

/**
 * A callback may be absolute — which is what this app sends — or
 * root-relative, which is Better Auth's own default of "/". The reference
 * origin exists only so the relative form can be parsed, and is dropped
 * again before returning.
 */
function tagged(target: string, locale: Locale): string {
  const parsed = new URL(target, 'https://locale.invalid')
  parsed.searchParams.set(LOCALE_PARAM, locale)
  return target.startsWith('/') ? `${parsed.pathname}${parsed.search}${parsed.hash}` : parsed.href
}
